import './Details.scss';
import axios from "axios";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FloatingBar from "../../../components/FloatingBar";
import NavigationBar from "../../../components/NavigationBar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import football_ball from "../../../assets/icons/football_ball.svg";
import video_svg from "../../../assets/icons/video.svg";
import coolman_logo from "../../../assets/images/coolman-logo-transparent.png"; 

function formatTime(isoString) {
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
}

function MatchDetails() {
    const { match_idx } = useParams();
    const API_URL = process.env.REACT_APP_API_URL;   
    const navigate = useNavigate();     
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('goals');
    const [matchDetails, setMatchDetails] = useState(null);
    const [quarters, setQuarters] = useState([])
    const [goals, setGoals] = useState([]);
    const [lineups, setLineups] = useState([]);     
    const [selectedQuarter, setSelectedQuarter] = useState(1);
    const [validPlayerImages, setValidPlayerImages] = useState({});
    const [selectedTeam, setSelectedTeam] = useState('A');
    
    const checkImageExists = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
        });
    };    
    
    const fetchData = async () => {
        setLoading(true);
        try {
            // Match details 요청은 항상 실행하고 성공적인 응답을 설정
            const matchResponse = await axios.get(`${API_URL}/matches/${match_idx}`);
            setMatchDetails(matchResponse.data);
    
            // 나머지 API 콜은 실패할 경우 빈 배열로 처리
            const [quarterResponse, goalsResponse, lineupResponse] = await Promise.allSettled([
                axios.get(`${API_URL}/matches/${match_idx}/quarters`),
                axios.get(`${API_URL}/matches/${match_idx}/goals`),
                axios.get(`${API_URL}/matches/${match_idx}/lineups`)
            ]);
    
            // Quarters API 결과 처리
            if (quarterResponse.status === "fulfilled") {
                setQuarters(quarterResponse.value.data);
            } else {
                console.warn("Quarters API failed:", quarterResponse.reason);
                setQuarters([]); // 빈 배열 설정
            }
    
            // Goals API 결과 처리
            if (goalsResponse.status === "fulfilled") {
                setGoals(goalsResponse.value.data);
            } else {
                console.warn("Goals API failed:", goalsResponse.reason);
                setGoals([]);
            }

            // Lineups API 결과 처리
            if (lineupResponse.status === "fulfilled") {
                const lineupsData = lineupResponse.value.data;
                
                setLineups(lineupsData);                

                const imageMap = {};
                await Promise.all(
                    lineupsData.map(async (player) => {
                        if (player.image_url) {
                            const exists = await checkImageExists(player.image_url);
                            imageMap[player.lineup_idx] = exists ? player.image_url : coolman_logo;
                        } else {
                            imageMap[player.lineup_idx] = coolman_logo;
                        }
                    })
                );
                setValidPlayerImages(imageMap);
            } else {
                console.warn("Lineups API failed:", lineupResponse.reason);
                setLineups([]);
                setValidPlayerImages({});
            }
                
        } catch (error) {
            console.error("Error fetching match details:", error);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchData();
    }, [match_idx]);

    const handleEdit = () => {        
        navigate(`/matches/${match_idx}/edit`);
    };

    const renderQuarters = () => {
        if (quarters.length === 0) {
            return <p>쿼터 정보가 없습니다.</p>;
        }

        const isNaeJeon = matchDetails.match_nature === '내전';
        let totalHomeScore = 0;
        let totalAwayScore = 0;

        const quartersWithGoals = quarters.map((quarter) => {
            const goalsInQuarter = goals.filter((goal) => goal.quarter_idx === quarter.quarter_idx);

            const quarterHomeScore = isNaeJeon
                ? goalsInQuarter.filter((g) => g.scoring_team === 'A').length
                : goalsInQuarter.filter((g) => g.goal_type === "득점").length;
            const quarterAwayScore = isNaeJeon
                ? goalsInQuarter.filter((g) => g.scoring_team === 'B').length
                : goalsInQuarter.filter((g) => g.goal_type === "실점" || g.goal_type === "자살골").length;

            return { ...quarter, goals: goalsInQuarter, quarterHomeScore, quarterAwayScore };
        });

        return quartersWithGoals.map((quarter) => (
            <div key={quarter.quarter_idx} className="quarter-group">
                <div className="quarter-header">
                    <h4>쿼터 {quarter.quarter_number}</h4>
                    <p>({quarter.quarterHomeScore} : {quarter.quarterAwayScore})</p>
                </div>
                {quarter.goals.length > 0 ? (
                    <ul className="goal-list">
                        {quarter.goals.map((goal) => {
                            if (isNaeJeon) {
                                if (goal.scoring_team === 'A') totalHomeScore += 1;
                                else totalAwayScore += 1;

                                const isATeam = goal.scoring_team === 'A';
                                const teamLabel = isATeam
                                    ? (matchDetails.team_a_name || 'A팀')
                                    : (matchDetails.team_b_name || 'B팀');

                                return (
                                    <li key={goal.goal_idx} className={`goal-item ${isATeam ? 'goal-left' : 'goal-right'}`}>
                                        <div className="goal-score-container">
                                            <img src={football_ball} alt="Football" className="goal-icon" />
                                            <span className="goal-score">{totalHomeScore} : {totalAwayScore}</span>
                                        </div>
                                        <div className="goal-description">
                                            <p className="goal-player" data-result={isATeam ? '득점' : '실점'}>
                                                [{teamLabel}] {goal.goal_player_name || 'N/A'}
                                            </p>
                                            {goal.assist_player_name && (
                                                <p className="assist-player">({goal.assist_player_name})</p>
                                            )}
                                        </div>
                                    </li>
                                );
                            }

                            if (goal.goal_type === "득점") totalHomeScore += 1;
                            else if (goal.goal_type === "실점" || goal.goal_type === "자살골") totalAwayScore += 1;

                            const playerName = goal.goal_type !== "실점" ? goal.goal_player_name || "N/A" : null;
                            const assistName = goal.goal_type === "득점" ? goal.assist_player_name : null;

                            return (
                                <li
                                    key={goal.goal_idx}
                                    className={`goal-item ${goal.goal_type === "득점" ? "goal-left" : "goal-right"}`}
                                >
                                    <div className="goal-score-container">
                                        <img src={football_ball} alt="Football" className="goal-icon" />
                                        <span className="goal-score">{totalHomeScore} : {totalAwayScore}</span>
                                    </div>
                                    <div className="goal-description">
                                        <p className="goal-player" data-result={goal.goal_type}>
                                            {goal.goal_type === "득점" ? playerName
                                                : goal.goal_type === "자살골" ? `${playerName} (자살골)`
                                                : "실점"}
                                        </p>
                                        {assistName && <p className="assist-player">({assistName})</p>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p>골 정보가 없습니다.</p>
                )}
            </div>
        ));
    };

    const renderLineups = () => {
        if (!quarters.length || !lineups.length) {
            return <p>라인업 정보가 없습니다.</p>;
        }

        const isNaeJeon = matchDetails.match_nature === '내전';

        const filteredLineups = lineups.filter(
            (lineup) => lineup.quarter_number === selectedQuarter
        );

        const teamLineups = isNaeJeon
            ? filteredLineups.filter((l) => l.lineup_team === selectedTeam)
            : filteredLineups;

        const startingPlayers = teamLineups.filter(player => player.lineup_status === '선발');
        const substitutePlayers = teamLineups.filter(player => player.lineup_status === '후보');

        const currentQuarterData = quarters.find(q => q.quarter_number === selectedQuarter);
        const displayTactics = isNaeJeon && selectedTeam === 'B'
            ? (currentQuarterData?.team_b_tactics || startingPlayers[0]?.tactics)
            : startingPlayers[0]?.tactics;

        return (
            <div className='lineup-group'>
                <div className="quarter-buttons">
                    {quarters.map((quarter) => (
                        <button
                            key={quarter.quarter_number}
                            className={`quarter-btn ${selectedQuarter === quarter.quarter_number ? "active" : ""}`}
                            onClick={() => setSelectedQuarter(quarter.quarter_number)}
                        >
                            {quarter.quarter_number}
                        </button>
                    ))}
                </div>
                {isNaeJeon && (
                    <div className="toggle-group" style={{ justifyContent: 'center', margin: '8px 0' }}>
                        <button
                            className={`toggle-btn ${selectedTeam === 'A' ? 'active' : ''}`}
                            onClick={() => setSelectedTeam('A')}
                        >
                            {matchDetails.team_a_name || 'A팀'}
                        </button>
                        <button
                            className={`toggle-btn ${selectedTeam === 'B' ? 'active' : ''}`}
                            onClick={() => setSelectedTeam('B')}
                        >
                            {matchDetails.team_b_name || 'B팀'}
                        </button>
                    </div>
                )}
                <div className="quarter-tactics">{displayTactics}</div>
                <div className="soccer-field">
                    {startingPlayers.map((player, index) => (
                        <div key={index} className="player-marker"
                            style={{
                                top: `${player.top_coordinate}%`,
                                left: `${player.left_coordinate}%`,
                            }}>
                            <div className="position-label">{player.position_name}</div>
                            <div
                                className="player-circle"
                                style={{
                                    backgroundImage: `url(${validPlayerImages[player.lineup_idx] || coolman_logo})`,
                                }}
                            ></div>
                            <div className="player-name">{player.user_name}</div>
                        </div>
                    ))}
                </div>
                <div className="lineup-container">
                    <div className="lineup-section">
                        <h3 className="section-title">후보</h3>
                        <table className="lineup-table">
                            <thead>
                                <tr>
                                    <th>등번호</th>
                                    <th>사람</th>
                                    <th>상태</th>
                                </tr>
                            </thead>
                            <tbody>
                                {substitutePlayers.map((lineup) => (
                                    <tr key={lineup.lineup_idx}>
                                        <td>{lineup.back_number}</td>
                                        <td>{lineup.user_name}</td>
                                        <td className="substitute">후보</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    
    const renderContent = () => {
        if (activeTab === "goals") {
            return renderQuarters();
        } else if (activeTab === "lineup") {
            return renderLineups();
        }
    };

    const renderResult = () => {
        const isNaeJeon = matchDetails.match_nature === '내전';
        const teamAName = matchDetails.team_a_name || 'A팀';
        const teamBName = matchDetails.team_b_name || 'B팀';
        return <>
        <div className="match-results">
            <div className="header-card" data-result={matchDetails.result}>
                <h2>경기결과</h2>
                <p data-result={matchDetails.result}>{matchDetails.result}</p>
            </div>
            <div className="card-container">
                <div className="card">
                    <span>스코어</span>
                    <p>{matchDetails.winning_point} : {matchDetails.losing_point}</p>
                </div>
                <div className="card">
                    <span>{isNaeJeon ? '팀' : '상대'}</span>
                    <p>{isNaeJeon ? `${teamAName} vs ${teamBName}` : matchDetails.opposing_team}</p>
                </div>
                <div className="card">
                    <span>날짜</span>
                    <p>{matchDetails.dt}</p>
                </div>
                <div className="card">
                    <span>시간</span>
                    <p>{`${formatTime(matchDetails.start_time)} ~ ${formatTime(matchDetails.end_time)}`}</p>
                </div>
                <div className="card">
                    <span>날씨</span>
                    <p>{matchDetails.weather}</p>
                </div>
                <div className="card">
                    <span>장소</span>
                    <p>{matchDetails.location}</p>
                </div>
                <div className="card">
                    <span>참가인원 (용병제외)</span>
                    <p>{matchDetails.num_players} 명</p>
                </div>
                <div className="card">
                    <span>메인전술</span>
                    <p>{matchDetails.main_tactics}</p>
                </div>
            </div>
        </div>
    </>
    }

    const renderMaterials = () => {
        if (!matchDetails.photo_url && !matchDetails.video_url) {
            return null;
        }

        return (
            <div className="match-materials">
                <div className="header-card">
                    <h2>경기자료</h2>
                </div>
                <div className="materials-content">
                    {matchDetails.photo_url && (
                        <div className="material-item">                    
                            <img src={matchDetails.photo_url} alt="경기 사진" className="match-photo" />
                        </div>
                    )}
                    {matchDetails.video_url && (
                        <div className="material-item">                       
                            <a
                                href={matchDetails.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="video-button"
                            >
                                <img src={video_svg} alt="Video" className="video-icon" />
                                <span>영상 보러가기</span>
                            </a>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="gray-background">
            <NavigationBar />
            <div className="content">
                {loading ? (
                    <LoadingSpinner />
                ) : matchDetails ? (
                    <>
                        {renderResult()}
                        <div className="match-details">
                            <div className="header-card">
                                <h2>경기상세</h2>
                                <div className="tabs">
                                    <button
                                        className={`tab-button ${activeTab === 'goals' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('goals')}
                                    >
                                        개요
                                    </button>
                                    <button
                                        className={`tab-button ${activeTab === 'lineup' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('lineup')}
                                    >
                                        라인업
                                    </button>
                                </div>
                            </div>
                            {renderContent()}
                        </div>
                        {renderMaterials()}
                    </>
                ) : (
                    <p>매치 상세 정보가 없습니다.</p>
                )}
            </div>
            <FloatingBar onEdit={handleEdit} mode='edit'/>
        </div>
    );
}

export default MatchDetails;
